import React from 'react';
import { Icon } from '../icons.jsx';
import {
  LizziLogo, Avatar, AvatarStack, StatusChip,
  Sidebar, UserMenu, MenuItem,
  Topbar, CommandPalette,
  ChatDock, ChatMessage, FormattedText, ChatBlock,
  ToastHost,
  ProgressBar, StatPill,
  fmtMoney, fmtPct, fmtDate, fmtDateFull,
} from '../components.jsx';
import { NewBidModal, UploadModal, NotificationsPanel, notify } from '../modals.jsx';
import { MockData } from '../mock/index.js';

const { useState, useEffect, useRef, useMemo, useCallback } = React;

// ===============================================
// Biblioteca — plantillas · cláusulas · memorias
// ===============================================
function LibraryScreen() {
  const { LIBRARY } = window.MockData;
  const [tab, setTab] = useState('clauses');

  return (
    <div className="viewport-inner fade-in">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Conocimiento corporativo</div>
          <h1 className="page-title">Biblioteca</h1>
          <p className="muted" style={{fontSize:14, marginTop:2, maxWidth:580}}>
            Plantillas reutilizables, cláusulas tipo y memorias previas. La base de conocimiento que alimenta a los asistentes.
          </p>
        </div>
        <div style={{display:'flex', gap:8}}>
          <button className="btn btn-outline" onClick={()=>notify('Importar entradas a la biblioteca', 'Upload')}><Icon.Upload size={14}/> Importar</button>
          <button className="btn btn-primary" onClick={()=>notify('Crear nueva entrada en la biblioteca', 'Plus')}><Icon.Plus size={14}/> Nueva entrada</button>
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:22}}>
        <KpiCard label="Cláusulas tipo"    value={LIBRARY.clauses.length} sub="Activas · con control de versiones"/>
        <KpiCard label="Plantillas"        value={LIBRARY.templates.length} sub="Por tipo de licitación"/>
        <KpiCard label="Memorias previas"  value={LIBRARY.memories.length} sub="Indexadas con embeddings"/>
      </div>

      <div className="tabs" style={{marginBottom:14}}>
        <div className={`tab ${tab==='clauses'?'active':''}`}    onClick={()=>setTab('clauses')}>Cláusulas <span style={{marginLeft:4, color:'var(--text-4)'}}>{LIBRARY.clauses.length}</span></div>
        <div className={`tab ${tab==='templates'?'active':''}`}  onClick={()=>setTab('templates')}>Plantillas <span style={{marginLeft:4, color:'var(--text-4)'}}>{LIBRARY.templates.length}</span></div>
        <div className={`tab ${tab==='memories'?'active':''}`}   onClick={()=>setTab('memories')}>Memorias previas <span style={{marginLeft:4, color:'var(--text-4)'}}>{LIBRARY.memories.length}</span></div>
      </div>

      {tab === 'clauses' && (
        <div className="card" style={{overflow:'hidden'}}>
          <table className="tbl">
            <thead><tr><th>Cláusula</th><th style={{width:200}}>Etiqueta</th><th style={{width:90, textAlign:'right'}}>Usos</th><th style={{width:130}}>Actualizada</th><th style={{width:36}}></th></tr></thead>
            <tbody>
              {LIBRARY.clauses.map(c => (
                <tr key={c.id} style={{cursor:'pointer'}}>
                  <td><div style={{display:'flex',alignItems:'center',gap:10}}><Icon.Quote size={14} style={{color:'var(--text-3)'}}/>{c.title}</div></td>
                  <td><span className="chip" style={{fontSize:11}}>{c.tag}</span></td>
                  <td style={{textAlign:'right', fontFamily:'JetBrains Mono'}}>{c.uses}</td>
                  <td style={{fontSize:12, color:'var(--text-3)'}}>{c.updated}</td>
                  <td><button className="btn btn-ghost btn-icon" onClick={()=>notify('Editar, duplicar o desactivar cláusula', 'MoreH')}><Icon.MoreH size={13}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'templates' && (
        <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12}}>
          {LIBRARY.templates.map(t => (
            <div key={t.id} className="card card-pad" style={{display:'flex',flexDirection:'column', gap:10, cursor:'pointer'}}>
              <div style={{display:'flex',alignItems:'center', justifyContent:'space-between'}}>
                <span className="chip">{t.type}</span>
                <span style={{fontSize:11, color:'var(--text-4)'}}>{t.used} usos</span>
              </div>
              <div style={{fontSize:14.5, fontWeight:500, lineHeight:1.4}}>{t.title}</div>
              <div style={{fontSize:12, color:'var(--text-3)'}}>{t.sections} secciones · estructura completa</div>
              <div style={{display:'flex', gap:6, marginTop:'auto'}}>
                <button className="btn btn-outline" style={{fontSize:12, padding:'5px 8px'}} onClick={()=>notify('Abriendo plantilla', 'FileText')}><Icon.FileText size={11}/> Abrir</button>
                <button className="btn btn-ghost" style={{fontSize:12, padding:'5px 8px'}} onClick={()=>notify('Plantilla clonada', 'Copy')}><Icon.Copy size={11}/> Clonar</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'memories' && (
        <div className="card" style={{overflow:'hidden'}}>
          <table className="tbl">
            <thead><tr><th>Memoria</th><th style={{width:180}}>Cliente</th><th style={{width:80}}>Año</th><th style={{width:80, textAlign:'right'}}>Pág.</th><th style={{width:140}}>CPV</th><th style={{width:110}}>Eval. interna</th><th style={{width:36}}></th></tr></thead>
            <tbody>
              {LIBRARY.memories.map(m => (
                <tr key={m.id} style={{cursor:'pointer'}}>
                  <td><div style={{display:'flex',alignItems:'center',gap:10}}><Icon.Book size={14} style={{color:'var(--text-3)'}}/>{m.title}</div></td>
                  <td style={{color:'var(--text-3)'}}>{m.client}</td>
                  <td className="font-mono" style={{fontSize:12, color:'var(--text-3)'}}>{m.year}</td>
                  <td style={{textAlign:'right', fontFamily:'JetBrains Mono'}}>{m.pages}</td>
                  <td className="font-mono" style={{fontSize:11.5, color:'var(--text-3)'}}>{m.cpv}</td>
                  <td><span className="sem sem-success" style={{fontSize:11}}><Icon.Star size={10}/> {m.score}</span></td>
                  <td><button className="btn btn-ghost btn-icon" onClick={()=>notify('Abrir memoria, exportar PDF, marcar referencia', 'MoreH')}><Icon.MoreH size={13}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

window.LibraryScreen = LibraryScreen;


export { LibraryScreen };
